import type { Point } from "@cs2/contracts";

export interface PathAnimationState {
  progress: number;
  finished: boolean;
}

export function pathAnimationState(
  elapsedMs: number,
  durationMs: number,
): PathAnimationState {
  if (
    !Number.isFinite(elapsedMs) ||
    !Number.isFinite(durationMs) ||
    durationMs <= 0
  ) {
    throw new RangeError("path animation duration must be positive");
  }
  const progress = Math.min(1, Math.max(0, elapsedMs) / durationMs);
  return { progress, finished: progress >= 1 };
}

export function pointAlongPath(
  path: readonly Point[],
  progress: number,
): Point | null {
  const first = path[0];
  if (!first) return null;
  if (path.length === 1) return first;

  let totalLength = 0;
  for (let index = 1; index < path.length; index++) {
    totalLength += Math.hypot(
      path[index]!.x - path[index - 1]!.x,
      path[index]!.y - path[index - 1]!.y,
    );
  }
  if (totalLength === 0) return first;

  let remaining = Math.min(1, Math.max(0, progress)) * totalLength;
  for (let index = 1; index < path.length; index++) {
    const from = path[index - 1]!;
    const to = path[index]!;
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    if (remaining <= length || index === path.length - 1) {
      const ratio = length === 0 ? 0 : remaining / length;
      return {
        x: from.x + (to.x - from.x) * ratio,
        y: from.y + (to.y - from.y) * ratio,
      };
    }
    remaining -= length;
  }
  return path.at(-1)!;
}
